import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import { CustomerSchema, type Customer } from '@/types/schemas';
import { createCustomer, updateCustomer } from './customerQueries';
import { logActivity } from '@/lib/activityLog';
import { useAuth } from '@/features/auth/AuthProvider';

export function CustomerFormDialog({
  open,
  onOpenChange,
  editing,
  initialPhone,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Customer | null;
  initialPhone?: string;
  onCreated?: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  type FormValues = z.infer<typeof CustomerSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(CustomerSchema),
    defaultValues: {
      name: '',
      phone: initialPhone ?? '',
      email: '',
      address: '',
      source: 'manual',
    },
    values: editing
      ? {
          name: editing.name,
          phone: editing.phone,
          email: editing.email ?? '',
          address: editing.address ?? '',
          source: editing.source ?? 'manual',
        }
      : undefined,
  });

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const parsed = CustomerSchema.parse(values);
      if (editing) {
        await updateCustomer(editing.id, parsed);
        return { id: editing.id, name: parsed.name };
      }
      const newId = await createCustomer(parsed);
      return { id: newId, name: parsed.name };
    },
    onSuccess: ({ id, name }) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      logActivity({
        action: 'customer_created',
        title: editing ? 'Customer Updated' : 'Customer Added',
        description: `${editing ? 'Updated' : 'Added'} customer ${name}`,
        actorId: user?.uid ?? null,
        actorName: user?.displayName ?? user?.email ?? null,
        entityType: 'customer',
        entityId: id,
      });
      toast({
        title: editing ? 'Customer updated' : 'Customer added',
        variant: 'success',
      });
      onOpenChange(false);
      form.reset();
      onCreated?.(id);
    },
    onError: (e) =>
      toast({ title: 'Save failed', description: String(e), variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit customer' : 'Add customer'}</DialogTitle>
          <DialogDescription>
            Customers are part of the CRM and reused across POS / Quotations.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="space-y-3"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input {...form.register('phone')} />
              {form.formState.errors.phone && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.phone.message}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" {...form.register('email')} />
          </div>
          <div className="space-y-1.5">
            <Label>Address</Label>
            <Textarea rows={2} {...form.register('address')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
